import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronUp, ChevronDown, Trash2, Plus, Save, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { authFetch, apiErr } from "../../lib/adminApi";
import { MODULE_SCHEMAS, MODULE_TYPES } from "../../data/moduleSchemas";
import { AdminBar } from "./AdminCore";
import { FieldEditor, inputCls, labelCls } from "./EditorFields";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminPageEditor() {
  const [params] = useSearchParams();
  const path = params.get("path") || "";
  const [spec, setSpec] = useState(null);
  const [open, setOpen] = useState(null);
  const [addType, setAddType] = useState("hero");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.title = "Admin · Page Editor | Cedar Views";
    fetch(`${API}/api/pages/spec?path=${encodeURIComponent(path)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setSpec)
      .catch(() => toast.error("Page not found"));
  }, [path]);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const res = await authFetch("/api/pages/spec", { method: "PUT", body: JSON.stringify(spec) });
      const data = await res.json();
      if (!res.ok) { toast.error(apiErr(data.detail)); return; }
      toast.success("Page saved — changes are live");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  }, [spec]);

  if (!spec) return <main className="pt-40 min-h-screen bg-[#050A15]" data-testid="editor-loading" />;

  const setModule = (i, mod) => setSpec({ ...spec, modules: spec.modules.map((m, x) => (x === i ? mod : m)) });
  const move = (i, dir) => {
    const v = [...spec.modules];
    const j = i + dir;
    if (j < 0 || j >= v.length) return;
    [v[i], v[j]] = [v[j], v[i]];
    setSpec({ ...spec, modules: v });
    setOpen(null);
  };
  const removeModule = (i) => {
    if (!window.confirm("Remove this section?")) return;
    setSpec({ ...spec, modules: spec.modules.filter((_, x) => x !== i) });
    setOpen(null);
  };
  const addModule = () => {
    const blank = JSON.parse(JSON.stringify(MODULE_SCHEMAS[addType].blank));
    setSpec({ ...spec, modules: [...spec.modules, blank] });
    setOpen(spec.modules.length);
  };

  return (
    <main className="pt-40 pb-24 min-h-screen bg-[#050A15]" data-testid="admin-page-editor">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
        <AdminBar title={`Editing ${spec.cta || spec.path}`} />
        <div className="flex flex-wrap items-center gap-3 mb-10">
          <button onClick={save} disabled={saving} data-testid="editor-save-btn" className="btn-solid disabled:opacity-40">
            <Save size={14} /> {saving ? "Saving…" : "Save & publish"}
          </button>
          <a href={spec.path} target="_blank" rel="noopener noreferrer" data-testid="editor-preview-link" className="btn-ghost">
            <ExternalLink size={14} /> View page
          </a>
          <span className="font-mono-x text-[10px] text-white/35">{spec.path}</span>
        </div>

        {/* PAGE SETTINGS */}
        <div className="border border-white/12 p-6 mb-8" data-testid="editor-page-settings">
          <p className="font-mono-x text-[10px] tracking-[0.25em] uppercase text-[#5B7F9E] mb-4">Page settings</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div><label className={labelCls}>Browser title (SEO)</label>
              <input value={spec.seoTitle || ""} onChange={(e) => setSpec({ ...spec, seoTitle: e.target.value })} data-testid="editor-seotitle" className={inputCls} /></div>
            <div><label className={labelCls}>Page name</label>
              <input value={spec.cta || ""} onChange={(e) => setSpec({ ...spec, cta: e.target.value })} data-testid="editor-cta" className={inputCls} /></div>
            <div><label className={labelCls}>Accent colour</label>
              <div className="flex gap-2 items-center">
                <input type="color" value={spec.theme?.accent || "#31465A"} onChange={(e) => setSpec({ ...spec, theme: { ...spec.theme, accent: e.target.value } })}
                  data-testid="editor-accent" className="h-9 w-12 bg-transparent border border-white/20 cursor-pointer" />
                <span className="font-mono-x text-xs text-white/50">{spec.theme?.accent}</span>
              </div></div>
            <div><label className={labelCls}>Brand name</label>
              <input value={spec.theme?.brand || ""} onChange={(e) => setSpec({ ...spec, theme: { ...spec.theme, brand: e.target.value } })} data-testid="editor-brand" className={inputCls} /></div>
          </div>
        </div>

        {/* MODULES */}
        <p className="font-mono-x text-[10px] tracking-[0.25em] uppercase text-[#5B7F9E] mb-4">Page sections ({spec.modules.length})</p>
        <div className="space-y-3" data-testid="editor-modules">
          {spec.modules.map((m, i) => {
            const schema = MODULE_SCHEMAS[m.type];
            return (
              <div key={`${m.type}-${i}`} className="border border-white/12">
                <div className="flex items-center justify-between px-5 py-3.5 cursor-pointer hover:bg-white/[0.03]"
                  onClick={() => setOpen(open === i ? null : i)} data-testid={`module-header-${i}`}>
                  <div className="flex items-center gap-4 min-w-0">
                    <span className="font-mono-x text-[10px] text-white/35">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-white text-sm font-medium truncate">{schema?.label || m.type}</span>
                    {m.heading && <span className="text-white/40 text-xs truncate hidden md:inline">— {m.heading}</span>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`module-up-${i}`} className="p-1.5 text-white/40 hover:text-white disabled:opacity-20"><ChevronUp size={15} /></button>
                    <button onClick={() => move(i, 1)} disabled={i === spec.modules.length - 1} data-testid={`module-down-${i}`} className="p-1.5 text-white/40 hover:text-white disabled:opacity-20"><ChevronDown size={15} /></button>
                    <button onClick={() => removeModule(i)} data-testid={`module-delete-${i}`} className="p-1.5 text-white/40 hover:text-red-400"><Trash2 size={15} /></button>
                  </div>
                </div>
                {open === i && schema && (
                  <div className="border-t border-white/10 p-6 space-y-5" data-testid={`module-form-${i}`}>
                    {schema.fields.map((f) => (
                      <FieldEditor key={f.k} def={f} prefix={`m${i}`} value={m[f.k]}
                        onChange={(v) => setModule(i, { ...m, [f.k]: v })} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ADD MODULE */}
        <div className="flex flex-wrap items-center gap-3 mt-8 border border-dashed border-white/20 p-5" data-testid="editor-add-module">
          <select value={addType} onChange={(e) => setAddType(e.target.value)} data-testid="add-module-select"
            className={`${inputCls} bg-[#050A15] max-w-xs`}>
            {MODULE_TYPES.map((t) => <option key={t} value={t}>{MODULE_SCHEMAS[t].label}</option>)}
          </select>
          <button onClick={addModule} data-testid="add-module-btn" className="btn-solid"><Plus size={14} /> Add section</button>
        </div>

        <div className="mt-10">
          <button onClick={save} disabled={saving} data-testid="editor-save-btn-bottom" className="btn-solid disabled:opacity-40">
            <Save size={14} /> {saving ? "Saving…" : "Save & publish"}
          </button>
        </div>
      </div>
    </main>
  );
}
